import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormArray,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { CustomInputComponent } from '../../../../../shared/custom-input/custom-input.component';
import { CustomDragdropImageComponent } from '../../../../../shared/custom-dragdrop-image/custom-dragdrop-image.component';
import { TextSeparatorComponent } from '../../../../../shared/text-separator/text-separator.component';
import { SharedModule } from '../../../../../shared/shared.module';
import { EstablishmentService } from '../../../../../services/establishment.service';
import { AlertDialogService } from '../../../../../services/alert-dialog.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-third-step-add-establishment',
  standalone: true,
  templateUrl: './third-step-add-establishment.component.html',
  styleUrls: ['./third-step-add-establishment.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    MatInputModule,
    MatOptionModule,
    SvgIconComponent,
    SharedModule
  ]
})
export class ThirdStepAddEstablishmentComponent implements OnInit {
  @Input() form!: FormGroup;
  @Output() onSubmit = new EventEmitter<void>();

  addressList: any[] = [];
  addressImages: string[][] = [[]];
  addressLoading = false;
  addressSearched = false;
  private searchSubject = new Subject<string>();

  constructor(
    private establishmentService: EstablishmentService,
    private alertDialogService: AlertDialogService
  ) {}

  get adressesControl(): FormArray {
    return this.form.get('adresses') as FormArray;
  }

  ngOnInit(): void {
    if (this.adressesControl.length === 0) {
      this.addAddress();
    }
    // Debounce keystrokes: one request when the user pauses, not per character.
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((query) => this.searchAddress(query));
  }

  private searchAddress(query: string): void {
    if (!query || query.trim().length < 3) {
      this.addressList = [];
      this.addressSearched = false;
      this.addressLoading = false;
      return;
    }
    this.addressLoading = true;
    this.addressSearched = true;
    this.establishmentService.getAddress(query, 'france').subscribe({
      next: (res) => {
        // Keep only real, usable French addresses — drop country/region-only hits
        // (e.g. typing "France") that have no postcode and can't be a valid site address.
        this.addressList = (res || []).filter(
          (v: any) => v?.address?.country_code === 'fr' && !!v?.address?.postcode
        );
        this.addressLoading = false;
      },
      error: () => {
        this.addressList = [];
        this.addressLoading = false;
      },
    });
  }

  addAddress(): void {
    const group = new FormGroup({
      name: new FormControl('', Validators.required),
      adress: new FormControl('', Validators.required),
      pays: new FormControl('', Validators.required),
      codePostale: new FormControl('', Validators.required),
      regionCode: new FormControl('', Validators.required),
      regionName: new FormControl('', Validators.required),
      ville: new FormControl('', Validators.required),
      departmentName: new FormControl('', Validators.required),
      addressImages: new FormControl([]),
      cededx: new FormControl(''),
      firstPhoto: new FormControl(''),
      secondPhoto: new FormControl(''),
      lat: new FormControl(null),
      lon: new FormControl(null),
    });
    this.adressesControl.push(group);
    this.addressImages.push([]);
  }

  getAdresseFormated(option: any): string {
    const a = option?.address || {};
    const full = [
      `${a.house_number || ''} ${a.road || ''}`.trim(),
      `${a.postcode || ''} ${a.city || a.town || a.village || ''}`.trim(),
      a.country || ''
    ];
    return full.filter(Boolean).join(', ');
  }

  autoCompleteOnChange(event: any, index: number): void {
    const query = event?.target?.value;
    const group = this.adressesControl.at(index);

    group.get('adress')?.markAsTouched();
    group.get('regionCode')?.markAsTouched();

    // Editing the address invalidates any previous pick: the derived fields must come
    // from a selected suggestion, so free text (e.g. "France") can never be "valid".
    group.patchValue(
      {
        regionCode: '', regionName: '', codePostale: '', pays: '',
        ville: '', departmentName: '', lat: null, lon: null,
      },
      { emitEvent: false }
    );

    if (query && query.trim()) {
      this.searchSubject.next(query.trim());
    } else {
      this.addressList = [];
      this.addressSearched = false;
    }
  }

  addressSelected(item: any, index: number): void {
    const group = this.adressesControl.at(index);
    const address = item?.address || {};
    const city = address.city || address.town || address.village || '';
    const dept = address.county || city;
    const formatted = this.getAdresseFormated(item);

    group.patchValue({
      lat: +item.lat,
      lon: +item.lon,
      address: formatted,
      adress: formatted,
      codePostale: address.postcode || '',
      pays: address.country || '',
      ville: city,
      departmentName: dept
    });

    // No postcode → cannot resolve a region; skip to avoid geo.api.gouv.fr/regions/undefined.
    if (!address.postcode) {
      return;
    }

    this.establishmentService.getRegionByPostalCode(address.postcode).subscribe({
      next: (res) => {
        const code = res?.[0]?.codeRegion;
        if (!code) {
          return; // guard: prevents the /regions/undefined 404
        }
        group.patchValue({ regionCode: code });
        this.establishmentService.getRegionByCode(code).subscribe({
          next: (region) => group.patchValue({ regionName: region?.nom || '' }),
          error: () => {},
        });
      },
      error: () => {},
    });
  }

  deleteNewSite(index: number): void {
    this.alertDialogService.openAlertModal(
      'success',
      'Confirmation',
      'Êtes-vous sûr(e) de vouloir supprimer cette adresse ?',
      'Supprimer',
      'Annuler'
    )?.subscribe(res => {
      if (res) {
        this.addressImages.splice(index, 1);
        this.adressesControl.removeAt(index);
      }
    });
  }

  getImage(images: string[], index: number): void {
    const group = this.adressesControl.at(index);
    const list: string[] = [];

    group.patchValue({ firstPhoto: '', secondPhoto: '', addressImages: [] });

    if (images[0]) {
      group.get('firstPhoto')?.patchValue(images[0]);
      list.push(images[0]);
    }
    if (images[1]) {
      group.get('secondPhoto')?.patchValue(images[1]);
      list.push(images[1]);
    }

    group.get('addressImages')?.patchValue(list);
  }

  addressValidation(index: number): boolean {
    const control = this.adressesControl.at(index);
    return control.get('adress')?.valid === true && control.get('regionCode')?.valid === true;
  }
}
